import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';

/** Settings › Data: the owner's invoice forwarding address
 *  (inv-<token>@in.clearpathsmb.com, Resend Inbound). The address is minted
 *  on the first GET; "Get a new address" rotates it and the old one stops
 *  working at once. Pro-only: rendered inside UploadPage's Pro branch. */
export default function InvoiceEmailCard({ initialAddress }: { initialAddress?: string }) {
  const [address, setAddress] = useState<string | null>(initialAddress || null);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    if (initialAddress) return;
    axios.get('/api/invoices/inbox')
      .then((res) => setAddress(res.data.address))
      .catch(() => toast.error("Couldn't load your invoice address"));
  }, [initialAddress]);

  const copy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Copied');
    } catch {
      toast.error("Couldn't copy. Select the address and copy it by hand");
    }
  };

  const rotate = async () => {
    if (!window.confirm('Get a new address? The current one stops working right away, so update any forwarding rules that use it.')) return;
    setRotating(true);
    try {
      const res = await axios.post('/api/invoices/inbox/rotate');
      setAddress(res.data.address);
      toast.success('New address ready');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Couldn't get a new address");
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
        <h3 className="font-serif text-lg text-[#2D2A2E]">Forward invoices by email</h3>
      </div>
      <p className="font-sans text-sm text-[#6B6670] mb-4 max-w-2xl">
        Forward supplier invoices here as a PDF or a photo. They show up on your Costs page within a few minutes.
      </p>
      {address ? (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              readOnly
              value={address}
              aria-label="Your invoice address"
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 font-mono text-sm text-[#2D2A2E] border border-[#E6E2DE] rounded-[8px] h-11 px-3 bg-[#FAF9F7] focus:outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]"
            />
            <button
              onClick={copy}
              aria-label="Copy the invoice address"
              className="inline-flex items-center justify-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] h-11 px-5 font-sans font-medium text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-2"
            >
              <Copy size={16} strokeWidth={1.5} />
              Copy
            </button>
          </div>
          <button
            onClick={rotate}
            disabled={rotating}
            className="mt-3 font-sans text-xs text-[#6B6670] hover:text-[#4A2D5E] hover:underline disabled:opacity-50"
          >
            {rotating ? 'Getting a new address…' : 'Get a new address'}
          </button>
        </>
      ) : (
        <p className="font-sans text-sm text-[#6B6670]">Loading your address…</p>
      )}
    </div>
  );
}
